import { ref, nextTick } from "vue";
import { query } from "gql-query-builder";
import HttpClient from "project-blue-http-client";
import { GQL_QUERIES } from "../../utils/APIs";

// Paginated, mirroring DRAWER_ARTICLES below — the backend now returns { data, count }
// instead of every published article in one response (was exceeding AWS Lambda's 6MB
// response payload limit as the article table grew).
export const PUBLIC_ARTICLES = ref({ data: [], count: 0, loading: false, hasMore: false, page: 1 });
export const inProgress = ref(false);
export const reqSignal = ref(new AbortController());
export const articleRequestPercentage = ref(0);
const PUBLIC_ARTICLES_PAGE_SIZE = 50;

export const fields = [
  "id",
  "name",
  "publishDate",
  "authorName",
  "category",
  "countries",
  "companies",
  "elements",
  "products",
  "description",
  "feed",
  "opinion",
  "bannerImage",
  "externalUrl",
  "type",
  "authorAlias"
];
export const DRAWER_ARTICLES = ref({ data: [], count: 0, loading: false, hasMore: false, page: 1 });
const DRAWER_PAGE_SIZE = 5;

export const getPublicArticles = async (page = 1) => {
  const gqlQuery = GQL_QUERIES.GET_PUBLIC_ARTICLES;
  reqSignal.value.abort();
  reqSignal.value = new AbortController();

  // Only the first page drives the full-page loading state (spinner + upload/download
  // progress bar) — "load more" (page > 1) keeps existing articles on screen as-is and
  // only flips PUBLIC_ARTICLES.loading, so the component doesn't remount/reload, it just
  // shows an inline loading state on the "load more" control while appending underneath.
  const isInitialLoad = page === 1;
  if (isInitialLoad) {
    inProgress.value = true;
    articleRequestPercentage.value = 0;
  }

  PUBLIC_ARTICLES.value = {
    ...PUBLIC_ARTICLES.value,
    loading: true,
    data: isInitialLoad ? [] : PUBLIC_ARTICLES.value.data,
  };

  let client = new HttpClient(gqlQuery.baseUrl);
  let uploadInterval = null;
  let downloadInterval = null;
  client.get(`${gqlQuery.controller}/${gqlQuery.operation}?page=${page}&pageSize=${PUBLIC_ARTICLES_PAGE_SIZE}`, {
    onUploadProgress: (progressEvent) => {
      if (!isInitialLoad) return;
      const total = progressEvent.event.total;
      const loaded = progressEvent.event.loaded;

      if (total) {
        if (uploadInterval) {
          clearInterval(uploadInterval);
          uploadInterval = null;
        }
        articleRequestPercentage.value = Math.floor((loaded / total) * 50);
      } else {
        if (!uploadInterval) {
          uploadInterval = setInterval(() => {
            if (articleRequestPercentage.value < 50) {
              articleRequestPercentage.value += 1;
            }
          }, 50);
        }
      }
    },
    onDownloadProgress: (progressEvent) => {
      if (!isInitialLoad) return;
      const total = progressEvent.event.total;
      const loaded = progressEvent.event.loaded;
      if (total) {
        if (downloadInterval) {
          clearInterval(downloadInterval);
          downloadInterval = null;
        }
        articleRequestPercentage.value = Math.min(Math.floor((loaded / total) * 50) + 50, 98);
      } else {
        if (!downloadInterval) {
          downloadInterval = setInterval(() => {
            if (articleRequestPercentage.value < 80) {
              articleRequestPercentage.value += 1;
            }
          }, 50);
        }
      }
    }
  })
  .then(response => {
    if (uploadInterval) clearInterval(uploadInterval);
    if (downloadInterval) clearInterval(downloadInterval);

    const incoming = response?.data ?? [];
    PUBLIC_ARTICLES.value = {
      data: isInitialLoad ? incoming : [...PUBLIC_ARTICLES.value.data, ...incoming],
      count: response?.count ?? 0,
      loading: false,
      hasMore: (page * PUBLIC_ARTICLES_PAGE_SIZE) < (response?.count ?? 0),
      page,
    };

    if (isInitialLoad) {
      nextTick(() => {
        articleRequestPercentage.value = 100;
      });
      setTimeout(() => {
        inProgress.value = false;
        if (window.$loading) {
          window.$loading.finish();
        }
        articleRequestPercentage.value = 0;
      }, 500);
    }
  })
  .catch(error => {
    PUBLIC_ARTICLES.value = { ...PUBLIC_ARTICLES.value, loading: false };
    if (isInitialLoad) {
      inProgress.value = false;
      articleRequestPercentage.value = 0;
    }
    if (uploadInterval) clearInterval(uploadInterval);
    if (downloadInterval) clearInterval(downloadInterval);
  });
};

export const loadMorePublicArticles = async () => {
  if (PUBLIC_ARTICLES.value.loading || !PUBLIC_ARTICLES.value.hasMore) return;
  await getPublicArticles(PUBLIC_ARTICLES.value.page + 1);
};

export const getDrawerArticles = async (assetProfile, page = 1) => {
  const gqlQuery = GQL_QUERIES.LIST_ARTICLES_BY_ASSET;

  const elementCodes = (assetProfile.primary_Commodities ?? []).join(',');
  const companyId    = assetProfile.companyId ?? null;

  if (!elementCodes && !companyId) {
    DRAWER_ARTICLES.value = { data: [], count: 0, loading: false, hasMore: false, page: 1 };
    return;
  }

  const params = new URLSearchParams();
  if (elementCodes) params.set('elementCodes', elementCodes);
  if (companyId)    params.set('companyId', String(companyId));
  params.set('page',     String(page));
  params.set('pageSize', String(DRAWER_PAGE_SIZE));

  DRAWER_ARTICLES.value = {
    ...DRAWER_ARTICLES.value,
    loading: true,
    data: page === 1 ? [] : DRAWER_ARTICLES.value.data,
  };

  let client = new HttpClient(gqlQuery.baseUrl);
  let uploadInterval = null;
  let downloadInterval = null;

  client.get(`${gqlQuery.controller}/${gqlQuery.operation}?${params}`, {
    onUploadProgress: (progressEvent) => {
      const total = progressEvent.event.total;
      const loaded = progressEvent.event.loaded;
      if (total) {
        if (uploadInterval) { clearInterval(uploadInterval); uploadInterval = null; }
        articleRequestPercentage.value = Math.floor((loaded / total) * 50);
      } else {
        if (!uploadInterval) {
          uploadInterval = setInterval(() => {
            if (articleRequestPercentage.value < 50) articleRequestPercentage.value += 1;
          }, 50);
        }
      }
    },
    onDownloadProgress: (progressEvent) => {
      const total = progressEvent.event.total;
      const loaded = progressEvent.event.loaded;
      if (total) {
        if (downloadInterval) { clearInterval(downloadInterval); downloadInterval = null; }
        articleRequestPercentage.value = Math.min(Math.floor((loaded / total) * 50) + 50, 98);
      } else {
        if (!downloadInterval) {
          downloadInterval = setInterval(() => {
            if (articleRequestPercentage.value < 80) articleRequestPercentage.value += 1;
          }, 50);
        }
      }
    }
  })
      .then((response) => {
        if (uploadInterval) clearInterval(uploadInterval);
        if (downloadInterval) clearInterval(downloadInterval);

        const incoming = (response?.data ?? []).map(a => ({
          id:           a.id,
          name:         a.name,
          description:  a.description,
          type:         a.type,
          publish_Date: a.publish_Date,
          authors:      a.authors ?? [],
        }));

        DRAWER_ARTICLES.value = {
          data:    page === 1 ? incoming : [...DRAWER_ARTICLES.value.data, ...incoming],
          count:   response?.count ?? 0,
          loading: false,
          hasMore: (page * DRAWER_PAGE_SIZE) < (response?.count ?? 0),
          page,
        };
      })
      .catch((error) => {
        if (uploadInterval) clearInterval(uploadInterval);
        if (downloadInterval) clearInterval(downloadInterval);
        console.warn('[getDrawerArticles] error', error);
        DRAWER_ARTICLES.value = { ...DRAWER_ARTICLES.value, loading: false };
      });
};
export const changeColor = (hex, alpha) => {
  const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
